/**
 * Your account → Notifications (migration 093): which AI agent notices this
 * person gets, in every organisation where they are an owner or admin.
 * `PUT /partner/auth/account/notifications`, saved on choice. Security
 * notices are always sent (the server's PARTNER_SECURITY_ALWAYS_ON); the list
 * below says what they are in words.
 */
import { useSession } from '../auth/session';
import type { NoticeMode } from '../api/types';
import { Choices, ErrorNotice, useToast } from '../ui/kit';
import { useMutation, useQuery } from '../ui/hooks';

const OPTIONS: Array<{ value: NoticeMode; title: string; description: string }> = [
  { value: 'all', title: 'Every notice', description: 'An email each time an AI agent publishes, changes something, or accepts the Terms.' },
  { value: 'digest', title: 'Daily digest only', description: 'Those in one email a day.' },
  { value: 'security', title: 'Security only', description: 'Only the security notices listed below.' },
];

export const ALWAYS_SENT = [
  'New AI agent access (a token or an agent sign-in), or an agent giving up its access',
  'An agent trying things only a person may do',
  'Changes waiting for you to reject or apply',
  'Sign-in and email changes, and undo (“This wasn’t me”) links',
];

export function NotificationPrefs() {
  const { api } = useSession();
  const toast = useToast();
  const prefs = useQuery(() => api.getNoticePrefs(), ['notice-prefs']);
  const save = useMutation((mode: NoticeMode) => api.setNoticePrefs(mode));
  if (!prefs.data && !prefs.error) return null;
  return (
    <section className="panel panel-pad stack-sm" aria-labelledby="notices-h" style={{ marginTop: 16 }}>
      <h2 id="notices-h" className="h-sub">AI agent notices</h2>
      <ErrorNotice error={prefs.error ?? save.error} />
      {prefs.data ? (
        <>
          <p className="small muted" style={{ margin: 0 }}>
            {prefs.data.applies
              ? 'Emails about what AI agents do, in every organisation where you are an owner or admin.'
              : 'These apply once you are an owner or admin of an organisation.'}
          </p>
          <Choices
            legend="Send me"
            value={prefs.data.mode}
            options={OPTIONS.map((o) => ({ ...o, disabled: save.pending }))}
            onChange={async (mode) => {
              const previous = prefs.data;
              prefs.setData(previous ? { ...previous, mode } : previous);
              const res = await save.run(mode);
              if (res) toast('Notice setting saved');
              else prefs.setData(previous);
            }}
          />
          <div className="small" data-testid="always-sent">
            <strong>Always sent, whatever you choose:</strong>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {ALWAYS_SENT.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          </div>
        </>
      ) : null}
    </section>
  );
}
